import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeftIcon,
  CalendarDaysIcon,
  MapPinIcon,
  ClockIcon,
  BuildingOfficeIcon,
  ClipboardDocumentListIcon,
  ArrowDownTrayIcon,
  EnvelopeIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ChatBubbleBottomCenterTextIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { getOrganismeLabel, getReunionStatusBadge } from '../utils/agendaHelpers';
import { getReunionTypeShort } from '../utils/reunionTypes';
import AgendaReunionLogo from '../components/AgendaReunionLogo';
import DonnerPouvoirModal from '../components/DonnerPouvoirModal';

export default function ReunionUserDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [reunion, setReunion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingPouvoir, setDownloadingPouvoir] = useState(false);
  const [showPouvoirModal, setShowPouvoirModal] = useState(false);

  useEffect(() => {
    fetchReunion();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  const fetchReunion = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/reunions/mine/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReunion(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement de la réunion');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/reunions/mine/${id}/convocation-pdf`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.open(response.data.url, '_blank');
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors du téléchargement du PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadPouvoirPdf = async () => {
    try {
      setDownloadingPouvoir(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/reunions/mine/${id}/pouvoir/pdf`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.open(response.data.url, '_blank');
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors du téléchargement du PDF');
    } finally {
      setDownloadingPouvoir(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-honey-500" />
      </div>
    );
  }

  if (error || !reunion) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Link to="/events" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 mb-6">
          <ArrowLeftIcon className="h-4 w-4 mr-1" /> Retour à l&apos;agenda
        </Link>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-700">{error || 'Réunion introuvable'}</p>
        </div>
      </div>
    );
  }

  const badge = getReunionStatusBadge(reunion);
  const typeShort = getReunionTypeShort(reunion.type, reunion.organisme);
  const hasConvocationContent =
    reunion.dateConvocation
    || reunion.hasConvocationPdf
    || reunion.ordresDuJour
    || reunion.texteLibreConvocation;

  return (
    <div className="min-h-screen bg-slate-50 pb-12">
      {/* En-tête */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
          <Link
            to="/events"
            className="inline-flex items-center text-sm text-slate-500 hover:text-honey-600 transition-colors mb-5"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" /> Retour à l&apos;agenda
          </Link>

          <div className="flex items-start gap-4">
            <AgendaReunionLogo organisme={reunion.organisme} size="lg" />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-honey-100 text-honey-800">
                  {typeShort}
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  Réunion convoquée
                </span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.cls}`}>
                  {badge.label}
                </span>
                {reunion.fermee && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                    <LockClosedIcon className="h-3 w-3" />
                    Clôturée
                  </span>
                )}
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">
                {reunion.title}
              </h1>
              <p className="text-sm text-honey-700 font-medium mt-1">
                {getOrganismeLabel(reunion.organisme)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Informations pratiques */}
        <div className="bg-white shadow-sm rounded-xl border border-slate-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/60">
            <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">
              Informations pratiques
            </h2>
          </div>
          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-honey-50 flex items-center justify-center">
                <CalendarDaysIcon className="h-5 w-5 text-honey-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Date</p>
                <p className="text-sm text-slate-900 capitalize mt-0.5">{formatDate(reunion.date)}</p>
              </div>
            </div>

            {(reunion.heureDebut || reunion.heureFin) && (
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-honey-50 flex items-center justify-center">
                  <ClockIcon className="h-5 w-5 text-honey-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Horaires</p>
                  <p className="text-sm text-slate-900 mt-0.5">
                    {reunion.heureDebut || '—'}
                    {reunion.heureFin ? ` – ${reunion.heureFin}` : ''}
                  </p>
                </div>
              </div>
            )}

            {reunion.lieu && (
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-honey-50 flex items-center justify-center">
                  <MapPinIcon className="h-5 w-5 text-honey-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Lieu</p>
                  <p className="text-sm text-slate-900 mt-0.5">{reunion.lieu}</p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-honey-50 flex items-center justify-center">
                <BuildingOfficeIcon className="h-5 w-5 text-honey-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Organisme</p>
                <p className="text-sm text-slate-900 mt-0.5">{reunion.organisme}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Encart convocation */}
        {hasConvocationContent && (
          <div className="bg-white shadow-sm rounded-xl overflow-hidden border border-honey-100">
            <div className="px-6 py-4 bg-honey-50/70 border-b border-honey-100 flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <EnvelopeIcon className="h-5 w-5 text-honey-600" />
                  Convocation
                </h2>
                {reunion.dateConvocation && (
                  <p className="mt-1 text-sm text-slate-500">
                    Reçue le{' '}
                    {new Date(reunion.dateConvocation).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                )}
              </div>
              {reunion.hasConvocationPdf && (
                <button
                  type="button"
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-honey-600 text-white text-sm font-medium rounded-lg hover:bg-honey-700 disabled:opacity-50 transition-colors shadow-sm"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  {downloadingPdf ? 'Téléchargement…' : 'Télécharger la convocation (PDF)'}
                </button>
              )}
            </div>

            {reunion.hasConvocationPdf && (
              <div className="mx-6 mt-4 flex items-center gap-3 p-3 border rounded-lg border-green-200 bg-green-50/80">
                <DocumentTextIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-green-900 truncate">
                    {reunion.convocationPdfName || 'Document de convocation'}
                  </p>
                  <p className="text-xs text-green-700">PDF officiel de convocation</p>
                </div>
                <button
                  type="button"
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf}
                  className="p-2 text-green-700 hover:bg-green-100 rounded-lg flex-shrink-0 transition-colors"
                  title="Télécharger le PDF de convocation"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                </button>
              </div>
            )}

            <div className="p-6 space-y-8">
              {reunion.ordresDuJour && (
                <section>
                  <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide flex items-center gap-2 mb-3">
                    <ClipboardDocumentListIcon className="h-4 w-4 text-honey-600" />
                    Ordre du jour
                  </h3>
                  <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed bg-slate-50/80 rounded-lg p-4 border border-slate-100">
                    {reunion.ordresDuJour}
                  </div>
                </section>
              )}

              {reunion.texteLibreConvocation && (
                <section className={reunion.ordresDuJour ? 'pt-8 border-t border-slate-100' : ''}>
                  <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide flex items-center gap-2 mb-3">
                    <ChatBubbleBottomCenterTextIcon className="h-4 w-4 text-honey-600" />
                    Informations complémentaires
                  </h3>
                  <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed bg-slate-50/80 rounded-lg p-4 border border-slate-100">
                    {reunion.texteLibreConvocation}
                  </div>
                </section>
              )}
            </div>
          </div>
        )}

        {/* Pouvoir de représentation */}
        {reunion.canDonnerPouvoir && (
          <div className="bg-white shadow-sm rounded-xl border border-indigo-100 overflow-hidden">
            <div className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                  <UserGroupIcon className="h-5 w-5 text-indigo-600" />
                  Pouvoir de représentation
                </h2>
                <p className="text-sm text-slate-500 mt-1 max-w-lg">
                  Vous ne pouvez pas assister à cette réunion ? Désignez un autre adhérent convoqué pour vous représenter.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowPouvoirModal(true)}
                className="inline-flex items-center justify-center px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors shadow-sm flex-shrink-0"
              >
                <UserGroupIcon className="h-4 w-4 mr-2" />
                Donner pouvoir
              </button>
            </div>
          </div>
        )}

        {/* Pouvoir enregistré */}
        {reunion.monPouvoir && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-indigo-100">
              <h2 className="text-base font-semibold text-indigo-900">Pouvoir enregistré</h2>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-indigo-800">
                Vous serez représenté(e) par{' '}
                <strong>
                  {reunion.monPouvoir.mandataire.prenom} {reunion.monPouvoir.mandataire.nom}
                </strong>
                .
              </p>
              {reunion.monPouvoir.createdAt && (
                <p className="text-xs text-indigo-600">
                  Enregistré le{' '}
                  {new Date(reunion.monPouvoir.createdAt).toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </p>
              )}
              {reunion.monPouvoir.pdf?.hasPdf && (
                <button
                  type="button"
                  onClick={handleDownloadPouvoirPdf}
                  disabled={downloadingPouvoir}
                  className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  {downloadingPouvoir ? 'Téléchargement…' : 'Télécharger mon pouvoir (PDF)'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <DonnerPouvoirModal
        isOpen={showPouvoirModal}
        onClose={() => setShowPouvoirModal(false)}
        reunionId={id}
        onSuccess={fetchReunion}
      />
    </div>
  );
}
